/**
 * World event calendar (Lost Ark's field bosses and timed world events).
 * Everything is keyed to the wall clock in UTC, so every client computes the
 * same schedule without talking to anyone; the zone host only decides when
 * to spawn.
 */

export type EventKind = 'fieldBoss' | 'breach';

export interface EventDef {
  id: string;
  kind: EventKind;
  name: string;
  description: string;
  /** Zone ids the event can happen in (breaches rotate through the list). */
  zones: string[];
  /** Repeats every `periodMin` minutes, starting `offsetMin` after each period boundary. */
  periodMin: number;
  offsetMin: number;
  durationMin: number;
  /** Warning announced this many minutes before the start. */
  warnMin: number;
  /** Recommended character level. */
  level: number;
  /** Field boss monster and the pack that marks its lair. */
  boss?: string;
  packId?: string;
}

export const EVENTS: EventDef[] = [
  {
    id: 'thornback', kind: 'fieldBoss', name: 'Elder Thornback', description: 'The old boar of the Vale wakes and tramples the southern meadows.',
    zones: ['emberwild'], periodMin: 30, offsetMin: 0, durationMin: 15, warnMin: 5, level: 10, boss: 'elder_thornback', packId: 'ew_fieldboss',
  },
  {
    id: 'dunemaw', kind: 'fieldBoss', name: 'Dunemaw', description: 'A burrowing terror surfaces in the oasis arena of the Sunscar.',
    zones: ['sunscar'], periodMin: 30, offsetMin: 10, durationMin: 15, warnMin: 5, level: 19, boss: 'dunemaw', packId: 'ss_fieldboss',
  },
  {
    id: 'rimefang', kind: 'fieldBoss', name: 'Rimefang Alpha', description: 'The alpha of the frost wolves prowls its den above the frozen lake.',
    zones: ['frosthollow'], periodMin: 30, offsetMin: 20, durationMin: 15, warnMin: 5, level: 28, boss: 'rimefang_alpha', packId: 'fh_fieldboss',
  },
  {
    id: 'breach', kind: 'breach', name: 'Aether Breach', description: 'A tear in the sky spills rift-spawn into the fields. Hold the breach through three waves.',
    zones: ['emberwild', 'sunscar', 'frosthollow'], periodMin: 60, offsetMin: 40, durationMin: 10, warnMin: 5, level: 8,
  },
];

export interface Occurrence {
  def: EventDef;
  /** Start and end times (ms since epoch). */
  start: number;
  end: number;
  warnAt: number;
  zone: string;
  active: boolean;
}

const MIN = 60_000;

/** The current occurrence if one is running, otherwise the next one. */
export function occurrence(def: EventDef, now: number): Occurrence {
  const period = def.periodMin * MIN;
  const offset = def.offsetMin * MIN;
  const duration = def.durationMin * MIN;
  let start = Math.floor((now - offset) / period) * period + offset;
  if (now >= start + duration) start += period;
  const index = Math.floor((start - offset) / period);
  const zone = def.zones[((index % def.zones.length) + def.zones.length) % def.zones.length] ?? def.zones[0] ?? '';
  return { def, start, end: start + duration, warnAt: start - def.warnMin * MIN, zone, active: now >= start && now < start + duration };
}

/** Upcoming occurrences of every event (current ones first), sorted by start. */
export function calendar(now: number, count = 1): Occurrence[] {
  const out: Occurrence[] = [];
  for (const def of EVENTS) {
    let t = now;
    for (let i = 0; i < count; i++) {
      const o = occurrence(def, t);
      out.push(o);
      t = o.end;
    }
  }
  return out.sort((a, b) => a.start - b.start);
}

/** Occurrences touching `zone` that are running or within their warning window. */
export function relevant(zone: string, now: number): Occurrence[] {
  return EVENTS.map((d) => occurrence(d, now)).filter((o) => o.zone === zone && now >= o.warnAt && now < o.end);
}

export function fmtCountdown(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}
